import { api } from './apiClient';

export type TeamRoleApi = 'ADMINISTRADOR' | 'PROFISSIONAL' | 'RECEPCIONISTA';

/** Rótulos exibidos na aba "Perfis de Usuário". */
export type TeamRoleLabel = 'Administrador' | 'Profissional' | 'Recepcionista';

const LABEL_TO_API: Record<TeamRoleLabel, TeamRoleApi> = {
  Administrador: 'ADMINISTRADOR',
  Profissional: 'PROFISSIONAL',
  Recepcionista: 'RECEPCIONISTA',
};

const API_TO_LABEL: Record<TeamRoleApi, TeamRoleLabel> = {
  ADMINISTRADOR: 'Administrador',
  PROFISSIONAL: 'Profissional',
  RECEPCIONISTA: 'Recepcionista',
};

export function toApiRole(label: string): TeamRoleApi {
  return LABEL_TO_API[label as TeamRoleLabel] ?? 'PROFISSIONAL';
}

export function toRoleLabel(role?: string | null): TeamRoleLabel {
  return API_TO_LABEL[(role || '') as TeamRoleApi] ?? 'Administrador';
}

export type InvitationResponse = {
  id: string;
  tenantId?: string;
  email: string;
  name: string;
  role: TeamRoleApi;
  roleLabel: TeamRoleLabel;
  /** Link que o convidado abre para criar a senha. */
  inviteUrl: string;
  expiresAt: string;
  /** "queued": e-mail sai por SMTP; "not-configured": servidor sem SMTP, só o link vale. */
  emailDelivery?: 'queued' | 'not-configured';
};

export type ResolvedInvitation = {
  email: string;
  tenantId: string;
  name: string;
  role: TeamRoleApi;
  roleLabel: TeamRoleLabel;
  expiresAt: string;
};

export type TeamMember = {
  id: string;
  tenantId?: string;
  email: string;
  name: string;
  crp?: string | null;
  clinicName?: string | null;
  avatarUrl?: string | null;
  professionalPhoto?: string | null;
  role: TeamRoleApi;
  roleLabel: TeamRoleLabel;
  status: 'Ativo' | 'Aguardando Cadastro' | 'Inativo';
  expiresAt?: string;
  /** Convite pendente ("Aguardando Cadastro"): id do convite na API. */
  invitationId?: string;
};

export function createInvitation(payload: {
  email: string;
  name: string;
  role: string;
}): Promise<InvitationResponse> {
  return api.post<InvitationResponse>('/team/invitations', {
    email: payload.email,
    name: payload.name,
    role: toApiRole(payload.role),
  });
}

export function resolveInvitation(token: string): Promise<ResolvedInvitation> {
  return api.get<ResolvedInvitation>(
    `/team/invitations/resolve?token=${encodeURIComponent(token)}`,
    { skipAuth: true },
  );
}

export async function fetchTeamMembers(): Promise<TeamMember[]> {
  const members = await api.get<TeamMember[]>('/team/members');
  return members;
}

/** Lê o token de convite da URL (`?invite=...`). */
export function readInviteTokenFromUrl(search = window.location.search): string | null {
  const token = new URLSearchParams(search).get('invite');
  return token?.trim() || null;
}

/** Remove o `?invite=` da barra de endereços após consumir o convite. */
export function clearInviteTokenFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('invite');
  window.history.replaceState({}, '', url.pathname + url.search + url.hash);
}

/**
 * Bloco #59: link do convite pendente — o mesmo do e-mail e do modal de envio.
 * `renewed` = convite antigo/vencido que ganhou um link novo (o anterior parou de valer).
 */
export function fetchInvitationLink(invitationId: string): Promise<{ invitationId: string; email: string; name: string; roleLabel: string; inviteUrl: string; renewed: boolean }> {
  return api.get(`/team/invitations/${encodeURIComponent(invitationId)}/link`);
}

/** Bloco #58: cancela um convite pendente (só administrador). */
export function revokeInvitation(invitationId: string): Promise<{ success: boolean }> {
  return api.delete(`/team/invitations/${encodeURIComponent(invitationId)}`);
}

/** Bloco #58: exclui a conta de Profissional/Recepcionista (só administrador). */
export function removeTeamMember(userId: string): Promise<{ success: boolean }> {
  return api.delete(`/team/members/${encodeURIComponent(userId)}`);
}
